import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
    ChevronDown,
    ChevronUp,
    SlidersHorizontal,
    Grid,
    Edit,
    Trash2,
    GripVertical
} from 'lucide-react';

// Format price helper
const formatPrice = (price) => {
    if (!price) return "Contact Agent";
    const num = Number(price);
    if (isNaN(num)) return price;
    if (num >= 1000000) {
        return `$${(num / 1000000).toFixed(1)}M`;
    }
    return `$${Math.round(num).toLocaleString()}`;
};

const PropertyListView = (props) => {
    const {
        properties = [],
        handleSort,
        navigate,
        onPropertyClick,
        capitalizeEachWord: capitalizeEachWordProp,
        onEdit,
        basePath = '/property',
        sortConfig,
        onStageUpdate, // New prop
    } = props;

    const [openStageDropdownId, setOpenStageDropdownId] = useState(null);

    const STAGES = [
        "Authority Signed",
        "Marketing Preparation",
        "Ready to Launch",
        "On The Market",
        "Offer In",
        "Under Contract"
    ];
    // Fallback for capitalizeEachWord
    const capitalizeEachWord = capitalizeEachWordProp || ((str) => {
        if (!str) return '';
        return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
    });

    const STORAGE_KEY = 'collings:lms:propertyListView:visibleColumns:v1';

    const allColumns = useMemo(() => ([
        { id: 'address', label: 'Property Address', alwaysOn: true, headerClass: 'px-8 text-sm font-bold text-gray-900 w-[280px] min-w-[280px]', cellClass: 'px-8 text-sm font-bold text-[#36423F] w-[280px] min-w-[280px]' },
        { id: 'price', label: 'Price', headerClass: 'px-6 text-sm font-bold text-gray-900 whitespace-nowrap w-[140px] min-w-[140px]', cellClass: 'px-6 text-sm text-gray-600 font-medium whitespace-nowrap w-[140px] min-w-[140px]' },
        { id: 'type', label: 'Type', headerClass: 'px-6 text-sm font-bold text-gray-900 text-center w-[120px] min-w-[120px]', cellClass: 'px-6 text-sm text-gray-600 font-bold text-center whitespace-nowrap w-[120px] min-w-[120px]' },
        { id: 'beds', label: 'Bed', headerClass: 'px-4 text-sm font-bold text-gray-900 text-center w-[80px] min-w-[80px]', cellClass: 'px-4 text-sm text-gray-600 font-bold text-center w-[80px] min-w-[80px]' },
        { id: 'baths', label: 'Bath', headerClass: 'px-4 text-sm font-bold text-gray-900 text-center w-[80px] min-w-[80px]', cellClass: 'px-4 text-sm text-gray-600 font-bold text-center w-[80px] min-w-[80px]' },
        { id: 'parking', label: 'Parking', headerClass: 'px-4 text-sm font-bold text-gray-900 text-center w-[80px] min-w-[80px]', cellClass: 'px-4 text-sm text-gray-600 font-bold text-center w-[80px] min-w-[80px]' },
        { id: 'floor_area', label: 'Floor Area', headerClass: 'px-6 text-sm font-bold text-gray-900 text-center w-[110px] min-w-[110px]', cellClass: 'px-6 text-sm text-gray-600 font-bold text-center whitespace-nowrap w-[110px] min-w-[110px]' },
        { id: 'land_area', label: 'Land Area', headerClass: 'px-6 text-sm font-bold text-gray-900 text-center w-[110px] min-w-[110px]', cellClass: 'px-6 text-sm text-gray-600 font-bold text-center whitespace-nowrap w-[110px] min-w-[110px]' },
        { id: 'rent', label: 'Rent', headerClass: 'px-6 text-sm font-bold text-gray-900 text-center w-[110px] min-w-[110px]', cellClass: 'px-6 text-sm text-gray-600 font-bold text-center whitespace-nowrap w-[110px] min-w-[110px]' },
        { id: 'gross_yield', label: 'Gross Yield', headerClass: 'px-8 text-sm font-bold text-gray-900 bg-[#E0F4F6] w-[130px] min-w-[130px]', cellClass: 'px-8 text-sm font-extrabold text-[#36423F] bg-[#E0F4F6]/40 whitespace-nowrap w-[130px] min-w-[130px]' },
        { id: 'net_yield', label: 'Net Yield', headerClass: 'px-8 text-sm font-bold text-gray-900 bg-[#E0F4F6] w-[130px] min-w-[130px]', cellClass: 'px-8 text-sm font-extrabold text-[#36423F] bg-[#E0F4F6]/40 whitespace-nowrap w-[130px] min-w-[130px]' },
        { id: 'body_corp', label: 'Body Corp', headerClass: 'px-6 text-sm font-bold text-gray-900 text-center w-[120px] min-w-[120px]', cellClass: 'px-6 text-sm text-gray-600 font-bold text-center whitespace-nowrap w-[120px] min-w-[120px]' },
        { id: 'vendor_name', label: 'Vendor Name', headerClass: 'px-6 text-sm font-bold text-gray-900 text-center w-[150px] min-w-[150px]', cellClass: 'px-6 text-sm text-gray-600 font-bold text-center whitespace-nowrap w-[150px] min-w-[150px]' },
        { id: 'vendor_phone', label: 'Vendor Phone', headerClass: 'px-6 text-sm font-bold text-gray-900 text-center w-[150px] min-w-[150px]', cellClass: 'px-6 text-sm text-gray-600 font-bold text-center whitespace-nowrap w-[150px] min-w-[150px]' },
        { id: 'vendor_email', label: 'Vendor Email', headerClass: 'px-6 text-sm font-bold text-gray-900 text-center w-[180px] min-w-[180px]', cellClass: 'px-6 text-sm text-gray-600 font-bold text-center whitespace-nowrap w-[180px] min-w-[180px]' },
        { id: 'email', label: 'Email', headerClass: 'px-6 text-sm font-bold text-gray-900 text-center w-[180px] min-w-[180px]', cellClass: 'px-6 text-sm text-gray-600 font-bold text-center whitespace-nowrap w-[180px] min-w-[180px]' },
        { id: 'agent', label: 'Agent', headerClass: 'px-6 text-sm font-bold text-gray-900 text-center w-[160px] min-w-[160px]', cellClass: 'px-6 text-sm text-gray-600 font-medium text-center whitespace-nowrap w-[160px] min-w-[160px]' },
        { id: 'stage', label: 'Stage', headerClass: 'px-6 text-sm font-bold text-gray-900 text-center w-[160px] min-w-[160px]', cellClass: 'px-6 text-center relative w-[160px] min-w-[160px]' },
        { id: 'actions', label: 'Actions', alwaysOn: true, headerClass: 'px-8 text-sm font-bold text-gray-900 text-center relative whitespace-nowrap w-[120px] min-w-[120px]', cellClass: 'px-8 text-center w-[120px] min-w-[120px]' },
    ]), []);

    const defaultVisible = useMemo(() => ([
        'address',
        'price',
        'beds',
        'baths',
        'parking',
        'floor_area',
        'gross_yield',
        'stage',
        'agent',
        'actions',
    ]), []);

    const [visibleColumns, setVisibleColumns] = useState(defaultVisible);
    const [showColumnMenu, setShowColumnMenu] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed)) return;
            // Always enforce required columns
            const enforced = new Set(parsed);
            allColumns.filter(c => c.alwaysOn).forEach(c => enforced.add(c.id));

            // Reconstruct in the order saved in localStorage, filtering out any invalid IDs
            const allIds = allColumns.map(c => c.id);
            const ordered = parsed.filter(id => allIds.includes(id));

            // Append any 'alwaysOn' columns that might have been missing from saved state
            allColumns.filter(c => c.alwaysOn).forEach(c => {
                if (!ordered.includes(c.id)) {
                    if (c.id === 'actions') ordered.push(c.id);
                    else ordered.unshift(c.id);
                }
            });

            setVisibleColumns(ordered);
        } catch (_) {
            // ignore
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleColumns));
        } catch (_) {
            // ignore
        }
    }, [visibleColumns]);

    useEffect(() => {
        if (!showColumnMenu) return;
        const onDocMouseDown = (e) => {
            if (!menuRef.current) return;
            if (!menuRef.current.contains(e.target)) {
                setShowColumnMenu(false);
            }
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [showColumnMenu]);

    const isVisible = (id) => visibleColumns.includes(id);

    const toggleColumn = (id) => {
        const col = allColumns.find(c => c.id === id);
        if (col?.alwaysOn) return;
        setVisibleColumns((prev) => {
            if (prev.includes(id)) return prev.filter(x => x !== id);
            // Insert before 'actions' column if it exists to keep Actions at the end
            const actionsIdx = prev.indexOf('actions');
            if (actionsIdx !== -1) {
                const next = [...prev];
                next.splice(actionsIdx, 0, id);
                return next;
            }
            return [...prev, id];
        });
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;
        const items = Array.from(visibleColumns);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        setVisibleColumns(items);
    };

    const getFloorArea = (property) => {
        const v = property?.floor_area ?? property?.floorArea ?? property?.building_area ?? property?.area;
        if (v == null || v === '') return '—';
        return `${v} m²`;
    };

    const getLandArea = (property) => {
        const v = property?.land_area ?? property?.landArea ?? property?.land_size;
        if (v == null || v === '') return '—';
        return `${v} m²`;
    };

    const getPercent = (v) => {
        if (v == null || v === '') return '—';
        const num = Number(v);
        if (Number.isFinite(num)) return `${num.toFixed(1)}%`;
        return `${v}`.includes('%') ? v : `${v}%`;
    };

    const renderHeaderContent = (colId) => {
        const col = allColumns.find(c => c.id === colId);
        if (!col) return null;

        const sortKeys = {
            address: "address",
            price: "price",
            type: "property_type",
            beds: "beds",
            baths: "baths",
            parking: "parking",
            floor_area: "area",
            land_area: "land_area",
            rent: "rent",
            gross_yield: "gross_yield",
            net_yield: "net_yield",
            body_corp: "body_corp",
            vendor_name: "vendor_name",
            vendor_phone: "vendor_phone",
            vendor_email: "vendor_email",
            email: "email"
        };

        const sortKey = sortKeys[colId];

        if (colId === 'actions') {
            return (
                <div
                    className="flex items-center justify-center gap-2 cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowColumnMenu(v => !v);
                    }}
                >
                    <GripVertical className="w-3 h-3 text-gray-300 cursor-grab active:cursor-grabbing" />
                    Actions <Grid className="w-4 h-4 opacity-30" />

                    {showColumnMenu && (
                        <div
                            ref={menuRef}
                            className="absolute right-0 top-[54px] z-50 w-[520px] max-w-[calc(100vw-40px)] bg-white rounded-2xl border border-gray-100 shadow-xl p-5"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <div className="text-sm font-extrabold text-gray-900">Customize Columns</div>
                                <button type="button" onClick={() => setShowColumnMenu(false)} className="text-gray-400 hover:text-gray-700">✕</button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {allColumns.filter(c => !c.alwaysOn).map((c) => {
                                    const active = isVisible(c.id);
                                    return (
                                        <button
                                            key={c.id}
                                            type="button"
                                            onClick={() => toggleColumn(c.id)}
                                            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${active ? "bg-[#E0F4F6] border-[#B9E6EB] text-[#2D8F99]" : "bg-white border-gray-200 text-gray-600 hover:border-[#55C5D0] hover:text-[#55C5D0]"}`}
                                        >
                                            {c.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div
                className={`flex items-center gap-2 ${sortKey && handleSort ? 'cursor-pointer' : ''} ${colId === 'address' ? '' : 'justify-center'}`}
                onClick={(e) => {
                    if (!sortKey || !handleSort) return;
                    e.stopPropagation();
                    handleSort(sortKey);
                }}
            >
                <GripVertical className="w-3 h-3 text-gray-300 cursor-grab active:cursor-grabbing" />
                {col.label}
                {sortKey && (
                    <div className="flex flex-col -space-y-1.5">
                        <ChevronUp className={`w-3 h-3 ${sortConfig?.key === sortKey && sortConfig?.direction === "asc" ? "opacity-100 text-[#55C5D0]" : "opacity-30"}`} />
                        <ChevronDown className={`w-3 h-3 ${sortConfig?.key === sortKey && sortConfig?.direction === "desc" ? "opacity-100 text-[#55C5D0]" : "opacity-30"}`} />
                    </div>
                )}
            </div>
        );
    };

    const renderCellContent = (property, colId, idx) => {
        switch (colId) {
            case 'address': {
                // In selection mode (leads board) the row isn't a navigation
                // target, so keep it plain text.
                if (onPropertyClick) {
                    return (
                        <div className="truncate w-[216px]" title={property.address}>
                            {property.address}
                        </div>
                    );
                }
                // A real link so right-click / middle-click / cmd+click can open
                // the property in a new tab (Christian Gravias, Monday 13225498426).
                // stopPropagation keeps a plain left-click from also firing the
                // row onClick and navigating twice.
                const to = property.detailPath || `${basePath}/${property.id}`;
                return (
                    <Link
                        to={to}
                        state={{ property }}
                        onClick={(e) => e.stopPropagation()}
                        className="block truncate w-[216px] text-inherit no-underline hover:underline"
                        title={property.address}
                    >
                        {property.address}
                    </Link>
                );
            }
            case 'price': return formatPrice(property.price);
            case 'type': return capitalizeEachWord(property.property_type || property.type || '—');
            case 'beds': return property.beds ?? '—';
            case 'baths': return property.baths ?? '—';
            case 'parking': {
                const total = (Number(property.parkings) || 0)
                    + (Number(property.car_ports) || 0)
                    + (Number(property.open_spaces) || 0)
                    + (Number(property.parking_spaces) || 0);
                if (total) return total;
                return property.parking ?? '—';
            }
            case 'floor_area': return getFloorArea(property);
            case 'land_area': return getLandArea(property);
            case 'rent': return property.weekly_rent ? formatPrice(property.weekly_rent) : '—';
            case 'gross_yield': {
                const hasRent = property.weekly_rent != null && property.weekly_rent !== '';
                const val = property.est_gross_yield ?? property.gross_yield ?? property.grossYield;
                return (hasRent && val != null) ? getPercent(val) : 'N/A';
            }
            case 'net_yield': {
                const hasRent = property.weekly_rent != null && property.weekly_rent !== '';
                const val = property.est_net_yield ?? property.net_yield ?? property.netYield;
                return (hasRent && val != null) ? getPercent(val) : 'N/A';
            }
            case 'body_corp': return property.body_corp ?? property.bodyCorp ?? '—';
            case 'vendor_name': return capitalizeEachWord(property.vendor_name ?? property.vendorName ?? '—');
            case 'vendor_phone': return property.vendor_phone ?? property.vendorPhone ?? '—';
            case 'vendor_email': return property.vendor_email ?? property.vendorEmail ?? '—';
            case 'email': return property.email ?? '—';
            case 'stage':
                return (
                    <>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setOpenStageDropdownId(openStageDropdownId === property.id ? null : property.id);
                            }}
                            className={`px-5 py-2 rounded-full text-[10px] font-extrabold inline-flex items-center justify-between gap-2 tracking-tight min-w-[140px] transition-all hover:ring-2 hover:ring-offset-1 hover:ring-[#55C5D0] ${(property.status?.name || property.status || "").toLowerCase().includes("signed") || (property.status?.name || property.status || "").toLowerCase().includes("authority") ? "bg-[#B9E6EB] text-[#2D8F99]" :
                                (property.status?.name || property.status || "").toLowerCase().includes("prep") || (property.status?.name || property.status || "").toLowerCase().includes("marketing") ? "bg-[#D6EFF2] text-[#2D8F99]" :
                                    (property.status?.name || property.status || "").toLowerCase().includes("ready") ? "bg-[#005F6B] text-white" :
                                        (property.status?.name || property.status || "").toLowerCase().includes("market") ? "bg-[#007B8C] text-white" :
                                            (property.status?.name || property.status || "").toLowerCase().includes("offer") ? "bg-orange-100 text-orange-700" :
                                                (property.status?.name || property.status || "").toLowerCase().includes("contract") ? "bg-red-100 text-red-700" :
                                                    "bg-gray-100 text-gray-700"
                                }`}
                        >
                            <span className="truncate">
                                {capitalizeEachWord(property.status?.name || property.status || "Ready to Launch")}
                            </span>
                            <ChevronDown className={`w-3 h-3 transition-transform ${openStageDropdownId === property.id ? 'rotate-180' : ''}`} />
                        </button>

                        {openStageDropdownId === property.id && (
                            <>
                                <div className="fixed inset-0 z-[60]" onClick={(e) => { e.stopPropagation(); setOpenStageDropdownId(null); }} />
                                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-[70] w-48 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 overflow-hidden animate-in fade-in zoom-in duration-200">
                                    {STAGES.map((stage) => (
                                        <button
                                            key={stage}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (onStageUpdate) onStageUpdate(property.id, stage);
                                                setOpenStageDropdownId(null);
                                            }}
                                            className="w-full px-4 py-2.5 text-left text-xs font-bold text-gray-700 hover:bg-[#E0F4F6] hover:text-[#2D8F99] transition-colors flex items-center justify-between group"
                                        >
                                            {stage}
                                            {(property.status?.name || property.status || "").toLowerCase() === stage.toLowerCase() && (
                                                <div className="w-1.5 h-1.5 rounded-full bg-[#55C5D0]" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </>
                );
            case 'agent': return capitalizeEachWord(property.agent?.name || "Christian Gravias");
            case 'actions':
                return (
                    <div className="flex items-center justify-center gap-2">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onEdit) onEdit(property);
                                else navigate(`/properties/edit/${property.id}`);
                            }}
                            className="p-2.5 text-gray-400 hover:text-[#55C5D0] border border-gray-100 rounded-xl hover:border-[#55C5D0] hover:bg-white transition-all shadow-sm"
                        >
                            <Edit className="w-4 h-4" />
                        </button>
                    </div>
                );
            default: return null;
        }
    };

    return (
        <div className="w-full bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto w-full scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                <DragDropContext onDragEnd={onDragEnd}>
                    <table className="w-full min-w-max text-left border-separate border-spacing-y-[12px]">
                        <thead>
                            <Droppable droppableId="columns" direction="horizontal">
                                {(provided) => (
                                    <tr
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        style={{ background: '#00000008' }}
                                        className="h-[57px]"
                                    >
                                        {visibleColumns.map((colId, index) => {
                                            const col = allColumns.find(c => c.id === colId);
                                            return (
                                                <Draggable key={colId} draggableId={colId} index={index}>
                                                    {(provided, snapshot) => (
                                                        <th
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            className={`${col?.headerClass} ${snapshot.isDragging ? 'bg-white shadow-lg ring-1 ring-[#55C5D0]' : ''}`}
                                                            style={{
                                                                ...provided.draggableProps.style,
                                                                // Use flex for header content to handle GripVertical nicely
                                                                display: snapshot.isDragging ? 'flex' : 'table-cell',
                                                                alignItems: 'center'
                                                            }}
                                                        >
                                                            {renderHeaderContent(colId)}
                                                        </th>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </tr>
                                )}
                            </Droppable>
                        </thead>
                        <tbody className="">
                            {properties.map((property, idx) => (
                                <tr
                                    key={property.id}
                                    style={{ background: '#00000008' }}
                                    className="h-[57px] group hover:bg-gray-100 transition-colors cursor-pointer"
                                    onClick={() => {
                                        if (onPropertyClick) {
                                            onPropertyClick(property);
                                        } else if (navigate) {
                                            navigate(property.detailPath || `${basePath}/${property.id}`, { state: { property } });
                                        }
                                    }}
                                >
                                    {visibleColumns.map((colId) => {
                                        const col = allColumns.find(c => c.id === colId);
                                        return (
                                            <td key={colId} className={col?.cellClass}>
                                                {renderCellContent(property, colId, idx)}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </DragDropContext>
            </div>
        </div>
    );
};

export default PropertyListView;
